import  { useEffect, useMemo, useState } from "react";
import Switch from "@mui/material/Switch";
import { DataGrid } from "@mui/x-data-grid";

export default function MerakiDashboard() {
  const [tab, setTab] = useState(0);

  // networks
  const [networks, setNetworks] = useState([]);
  const [loadingNetworks, setLoadingNetworks] = useState(true);
  const [netErr, setNetErr] = useState("");

  const [mxStatus, setMxStatus] = useState(null);
  const [loadingMx, setLoadingMx] = useState(false);

  const [mt40s, setMt40s] = useState([]);
  const [loadingMt40s, setLoadingMt40s] = useState(false);

  // per-network
  const [networkId, setNetworkId] = useState("");
  const [ssids, setSsids] = useState([]);
  const [loadingSsids, setLoadingSsids] = useState(false);
  const [busy, setBusy] = useState(false);

  // global
  const [clients, setClients] = useState([]);
  const [vpn, setVpn] = useState([]);
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [globalErr, setGlobalErr] = useState("");

  const activeNetworkName = useMemo(
    () => networks.find(n => n.id === networkId)?.name || "",
    [networks, networkId]
  );

  // ----------------- load networks -----------------
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("/api/Meraki/GetMerakiNetworks");
        if (!r.ok) throw new Error();
        const data = await r.json();
        setNetworks(Array.isArray(data) ? data : []);
      } catch {
        setNetErr("Failed to load networks");
      } finally {
        setLoadingNetworks(false);
      }
    })();
  }, []);

  // ----------------- per-network SSIDs -------------
  const loadSsids = async (id) => {
    if (!id) return;
    setLoadingSsids(true);
    try {
      const r = await fetch(`/api/Meraki/networks/${encodeURIComponent(id)}/ssids`);
      if (!r.ok) throw new Error();
      const data = await r.json();
      setSsids(Array.isArray(data) ? data : []);
    } catch {
      setSsids([]);
      alert("Could not load SSIDs for that network.");
    } finally {
      setLoadingSsids(false);
    }
  };

  const loadMxStatus = async (id) => {
    if (!id) return;
    setLoadingMx(true);
    try {
      const r = await fetch(`/api/Meraki/networks/${encodeURIComponent(id)}/mx/status`);
      if (!r.ok) throw new Error();
      const data = await r.json();
      setMxStatus(data);
    } catch {
      setMxStatus(null);
    } finally {
      setLoadingMx(false);
    }
  };

    const loadMt40s = async (id) => {
    if (!id) return;
    setLoadingMt40s(true);
    try {
      const r = await fetch(`/api/Meraki/networks/${encodeURIComponent(id)}/devices`);
      if (!r.ok) throw new Error();
      const data = await r.json();
      // filter to MT40 devices
      const onlyMt40 = Array.isArray(data) ? data.filter(d => (d.model || "").toUpperCase().startsWith("MT40")) : [];
      setMt40s(onlyMt40);
    } catch {
      setMt40s([]);
    } finally {
      setLoadingMt40s(false);
    }
  };

  const toggleSsid = async (ssid) => {
    if (!networkId) return;
    setBusy(true);
    try {
      const r = await fetch(
        `/api/Meraki/networks/${encodeURIComponent(networkId)}/ssids/${ssid.number}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enabled: !ssid.enabled }),
        }
      );
      if (!r.ok) throw new Error();
      await loadSsids(networkId);
    } catch {
      alert("Failed to update SSID.");
    } finally {
      setBusy(false);
    }
  };

  const powerCycleCabinet = async () => {
    if (!networkId) return;


    if (!window.confirm(`Power cycle the cabinet for\n${activeNetworkName}?`)) return;
    setBusy(true);
    try {
      const r = await fetch(
        `/api/Meraki/networks/${encodeURIComponent(networkId)}/cabinet/powercycle`,
        { method: "POST" }
      );
      if (!r.ok) throw new Error();
      alert("Power cycle command sent.");
    } catch {
      alert("Power cycle failed.");
    } finally {
      setBusy(false);
    }
  };

  const swapMx = async () => {
    if (!networkId) return;
    setBusy(true);
    try {
      const r = await fetch(
        `/api/Meraki/networks/${encodeURIComponent(networkId)}/mx/swap`,
        { method: "POST" }
      );
      if (!r.ok) throw new Error();
      
      await loadMxStatus(networkId);

      alert("Swap command sent.");
    } catch {
      alert("Swap failed.");
    } finally {
      setBusy(false);
    }
  };

  // ----------------- global data -------------------
  const loadGlobal = async () => {
    setLoadingGlobal(true);
    setGlobalErr("");
    try {
      const [cRes, vRes] = await Promise.all([
        fetch("/api/Meraki/networks/clients"),
        fetch("/api/Meraki/networks/vpn-statuses"),
      ]);
      if (!cRes.ok || !vRes.ok) throw new Error();
      const [cRaw, vRaw] = [await cRes.json(), await vRes.json()];

      const c = Array.isArray(cRaw) ? cRaw : cRaw.body || [];
      const v = Array.isArray(vRaw) ? vRaw : vRaw.body || [];
      setClients(c);
      setVpn(v);
    } catch {
      setGlobalErr("Failed to load global data");
    } finally {
      setLoadingGlobal(false);
    }
  };

  // ----------------- UI ----------------------------
  return (
    <div style={sx.page}>
      <h1 style={sx.h1}>Meraki – Network Page</h1>

      <div style={sx.tabs}>
        <button
          onClick={() => setTab(0)}
          style={{ ...sx.tab, ...(tab === 0 ? sx.tabActive : {}) }}
        >
          Per Network
        </button>
        <button
          onClick={() => {
            setTab(1);
            if (!clients.length && !vpn.length) loadGlobal();
          }}
          style={{ ...sx.tab, ...(tab === 1 ? sx.tabActive : {}) }}
        >
          Global
        </button>
      </div>

      {tab === 0 && (
        <div style={{ display: "grid", gap: 16 }}>
          {/* Network Picker */}
          <div style={sx.card}>
            <div style={sx.cardTitle}>Select Network (Drop-down List)</div>
            {loadingNetworks ? (
              <div>Loading…</div>
            ) : netErr ? (
              <div style={sx.error}>{netErr}</div>
            ) : (
              <select
                style={sx.select}
                value={networkId}
                onChange={(e) => {
                  const id = e.target.value;
                  setNetworkId(id);
                  loadSsids(id);
                  loadMxStatus(id);  
                  loadMt40s(id);
                }}
              >
                <option value="">— choose a network —</option>
                {networks.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Three blocks like the mockup */}
          <div style={sx.columns}>
            {/* SSIDs */}
            <div style={sx.card}>
              <div style={sx.cardTitle}>View SSID</div>
              {!networkId ? (
                <div style={sx.muted}>Select a network to view SSIDs.</div>
              ) : loadingSsids ? (
                <div>Loading SSIDs…</div>
              ) : ssids.length === 0 ? (
                <div style={sx.muted}>No SSIDs.</div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {ssids.map((s) => (
                    <div key={s.number} style={sx.row}>
                      <span>{s.name}</span>
                      <div style={sx.toggleRow}>
                         <span style={sx.mini}>{s.enabled ? "ON" : "OFF"}</span>
                          <Switch
                          checked={!!s.enabled}
                          onChange={() => toggleSsid(s)}
                          disabled={busy}
                          size="small"
                          color="primary"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Power Cycle */}
        <div style={sx.card}>
          <div style={sx.cardTitle}>Power Cycle the Cabinet</div>

          {!networkId ? (
            <div style={sx.muted}>Select a network.</div>
          ) : loadingMt40s ? (
            <div>Finding MT40s…</div>
          ) : mt40s.length === 0 ? (
            <div style={sx.muted}>No MT40 found in this network.</div>
          ) : (
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
              Targeting {mt40s.length} MT40{mt40s.length > 1 ? "s" : ""}:
              {" "}
              {mt40s.map(m => m.name || m.serial).join(", ")}
            </div>
          )}

        <button
          style={sx.resetBtn}
          onClick={powerCycleCabinet}
          disabled={!networkId || busy || mt40s.length === 0}
        >
          RESET
        </button>
      </div>


            {/* MX swap */}
            <div style={sx.card}>
              <div style={sx.cardTitle}>Switch MX Priority</div>
              {!networkId ? (
                <div style={sx.muted}>Select a network.</div>
              ) : loadingMx ? (
                <div>Loading MX status…</div>
              ) : mxStatus?.enabled ? (
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
                Current Primary:{" "}
                {mxStatus.currentPrimarySerial || "Unknown"} /
                {mxStatus.currentPrimaryName ? ` (${mxStatus.currentPrimaryName})` : ""}
                <br />
                Current Spare:{" "}
                {mxStatus.currentSpareSerial || "Unknown"} /
                {mxStatus.currentSpareName ? ` (${mxStatus.currentSpareName})` : ""}
              </div>

              ) : (
                <div style={sx.muted}>Warm spare not enabled for this network.</div>
              )}
              <button
                style={sx.primaryBtn}
                onClick={swapMx}
                disabled={!networkId || busy}
              >
                Swap Primary ↔ Secondary
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 1 && (
        <div style={{ display: "grid", gap: 16 }}>
          {/* Clients table */}
          {/* Clients table */}
<div style={sx.card}>
  <div style={sx.cardTitle}>Devices per SSID / VLAN (All Networks)</div>
  {loadingGlobal ? (
    <div>Loading…</div>
  ) : globalErr ? (
    <div style={sx.error}>{globalErr}</div>
  ) : (
    <div style={{ height: 500, width: "100%" }}>
      <DataGrid
        rows={clients.map((c, i) => ({
          id: i,
          network: c.networkName,
          name: c.description || c.mac,
          manufacturer: c.manufacturer || "-",
          ip: c.ip || "-",
          ssid: c.ssid || "-",
          vlan: c.vlan ?? "-",
        }))}
        columns={[
          { field: "network", headerName: "Network", flex: 1 },
          { field: "name", headerName: "Device Name", flex: 1 },
          { field: "manufacturer", headerName: "Manufacturer", flex: 1 },
          { field: "ip", headerName: "IPv4", flex: 1 },
          { field: "ssid", headerName: "SSID", flex: 1 },
          { field: "vlan", headerName: "VLAN", flex: 1 },
        ]}
        pageSize={25}
        rowsPerPageOptions={[25, 50, 100]}
        disableSelectionOnClick
        autoHeight={false}
      />
    </div>
  )}
</div>


          {/* VPN table */}
          {/* VPN table */}
<div style={sx.card}>
  <div style={sx.cardTitle}>Meraki VPN Status</div>
  {loadingGlobal ? (
    <div>Loading…</div>
  ) : globalErr ? (
    <div style={sx.error}>{globalErr}</div>
  ) : (
    <div style={{ height: 400, width: "100%" }}>
      <DataGrid
        rows={vpn.map((v, i) => ({
          id: i,
          network: v.networkName,
          status: v.status ?? "",
          tunnels: (v.tunnels ?? [])
            .map((t) => `${t.peerName} (${t.status})`)
            .join(", "),
        }))}
        columns={[
          { field: "network", headerName: "Network", flex: 1 },
          { field: "status", headerName: "Status", flex: 1 },
          { field: "tunnels", headerName: "Tunnels", flex: 2 },
        ]}
        pageSize={25}
        rowsPerPageOptions={[25, 50, 100]}
        disableSelectionOnClick
        autoHeight={false}
      />
    </div>
  )}
</div>
        </div>
      )}
    </div>
  );
}

/* ---------- tiny table component ---------- */
function Table({ columns, rows }) {
  return (
       <div style={sx.tableContainer}>

      <table style={sx.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} style={sx.th}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((cell, j) => (
                <td key={j} style={sx.td}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
/* ---------- Palantir-inspired light theme ---------- */
const sx = {
  page: {
    minHeight: "100vh",
    background: "#f9fafb",          // light neutral background
    color: "#111827",              // near-black text
    padding: 24,
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial",
  },
  h1: { fontSize: 24, fontWeight: 700, marginBottom: 16, color: "#0f172a" },

  tabs: { display: "flex", gap: 8, marginBottom: 20 },
  tab: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "white",
    color: "#374151",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    transition: "all 0.15s ease",
  },
  tableContainer: {
  overflowX: "auto",
  overflowY: "auto",
  maxHeight: 400, // px, adjust as needed
  border: "1px solid #e5e7eb",
  borderRadius: 8,
},
  tabActive: {
    borderColor: "#2563eb",
    background: "#eff6ff",
    color: "#1d4ed8",
    boxShadow: "0 0 0 2px rgba(37,99,235,0.2)",
  },

  columns: {
    display: "grid",
    gap: 20,
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  },

  card: {
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 4px 12px rgba(0,0,0,0.04)", // glassy soft shadow
    transition: "box-shadow 0.2s ease",
  },
  cardTitle: { fontWeight: 600, marginBottom: 10, color: "#111827" },

  select: {
    width: 320,
    background: "white",
    color: "#111827",
    padding: 8,
    borderRadius: 8,
    border: "1px solid #d1d5db",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },

  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid #f3f4f6",
  },
  toggleRow: { display: "flex", alignItems: "center", gap: 10 },
  mini: { fontSize: 12, color: "#6b7280" },

  resetBtn: {
    padding: "12px 24px",
    borderRadius: 999,
    border: "1px solid #d1d5db",
    background: "#f9fafb",
    color: "#111827",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  primaryBtn: {
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.15s ease",
  },

  error: { color: "#dc2626", fontWeight: 500 },
  muted: { color: "#6b7280" },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  },
  th: {
    textAlign: "left",
    borderBottom: "1px solid #e5e7eb",
    padding: "10px 8px",
    fontWeight: 600,
    color: "#374151",
    background: "#f9fafb",
  },
  td: {
    borderBottom: "1px solid #f3f4f6",
    padding: "10px 8px",
    color: "#374151",
    whiteSpace: "nowrap",
  },

  // Switch
  switch: {
    position: "relative",
    display: "inline-block",
    width: 44,
    height: 24,
  },
  slider: {
    position: "absolute",
    cursor: "pointer",
    inset: 0,
    backgroundColor: "#d1d5db",
    borderRadius: 999,
    transition: "0.2s",
  },
};
sx.slider.__proto__.toString = function () {
  return "";
};
